import React from 'react';
import type { IconV2Props } from '@withjoy/joykit';
import { Badge, Flex, TextV2, Divider } from '@withjoy/joykit';
import type { MenuSectionProps } from './MenuSection';
import type { TypographyVariant, Colors } from '@withjoy/joykit/theme';
import { pxToRem } from '@withjoy/joykit/theme';
import { StyledItemWrapper } from './BurgerMenu.styles';
import { NewBadge } from '@apps/admin/routes/Dashboard/components/DashboardContentV2/PrimaryDashboardContent/ProductRowItem/ProductRowItem.styles';
import { NotificationPill } from '@shared/components/NotificationPill';
import { ChevronRight, LockFilled, People, WarningTriangle } from '@withjoy/joykit/icons';
import { Link, useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { EventPageVisibility } from '@graphql/generated-base';
import { HiddenLabel } from '@apps/admin/common/HiddenLabel/HiddenLabel';

export type MenuItemProps = {
  icon?: React.ComponentType<IconV2Props>;
  text: string;
  badge?: React.FC<IconV2Props>;
  newPillText?: string;
  notificationPill?: string;
  /** Neutral right-aligned counter badge (e.g. messaging Drafts/Scheduled counts). Omit or 0 to hide. */
  count?: number;
  /** Render `count` as a red warning pill (e.g. failed-scheduled messages) instead of the neutral counter. */
  countIsWarning?: boolean;
  subItems?: Omit<MenuSectionProps, 'openSubmenu' | 'onClose'>[];
  onClick?: () => void;
  topDivider?: boolean;
  bottomDivider?: boolean;
  typographyVariant?: TypographyVariant;
  fontWeight?: number;
  color?: keyof Colors;
  columnGap?: number;
  iconSize?: 'sm' | 'md';
  expands?: boolean;
  isDisabled?: boolean;
  pageVisibility?: `${EventPageVisibility}`;
  // Real container visibility; pageVisibility collapses specificGuests→hidden. Set only when the
  // per-guest flag is on, so its mere presence means "treat as specificGuests".
  containerVisibility?: `${EventPageVisibility}`;
  path?: string;
  pathIsInJoyWeb?: boolean;
  openSubmenu?: (items: Omit<MenuSectionProps, 'openSubmenu' | 'onClose'>[]) => void;
  onClose?: () => void;
};

export const MenuItem: React.FC<MenuItemProps> = ({
  icon,
  text,
  onClick,
  newPillText,
  topDivider,
  bottomDivider,
  typographyVariant = 'body1',
  fontWeight = 500,
  color = 'mono14',
  iconSize,
  columnGap = 16,
  expands,
  notificationPill,
  count,
  countIsWarning,
  isDisabled,
  pageVisibility,
  containerVisibility,
  path,
  pathIsInJoyWeb,
  subItems,
  openSubmenu,
  onClose,
}) => {
  const handleOnClick = useEventCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (subItems?.length) {
      openSubmenu?.(subItems!);
    } else {
      onClick?.();
      if (!(e.metaKey || e.ctrlKey)) onClose?.(); // To prevent close menu panel if clicked with cmd/ctr.
    }
  });
  const Icon = icon;
  const { tq } = useTranslation('adminDashboardV3');
  // specificGuests pages collapse to pageVisibility=hidden; the real value lives on containerVisibility
  // (set only when the per-guest flag is on). They're visible to matched guests, so they must not read
  // as hidden.
  const isSpecificGuests = containerVisibility === EventPageVisibility.specificGuests;
  const isHidden = !isSpecificGuests && (pageVisibility === EventPageVisibility.hidden || (isDisabled && pageVisibility !== EventPageVisibility.passwordProtected));
  const effectiveColor = isHidden ? 'mono8' : color;

  const visibilityIcon = (() => {
    if (isSpecificGuests) {
      return (
        <People
          size={'sm'}
          color="mono14"
          title={tq`Specific Guests`}
          aria-label={tq`Specific Guests`}
        />
      );
    }
    switch (pageVisibility) {
      case EventPageVisibility.passwordProtected:
        return (
          <LockFilled
            size={'sm'}
            color="mono14"
            title={tq`Password Protected`}
            aria-label={tq`Password Protected`}
          />
        );
      case EventPageVisibility.hidden:
        return <HiddenLabel label={tq`Hidden`} />;
      default:
        return isDisabled ? <HiddenLabel label={tq`Hidden`} /> : null;
    }
  })();

  const content = (
    <Flex
      paddingY={pxToRem(12)}
      alignItems={'center'}
      columnGap={pxToRem(columnGap)}
      position="relative"
    >
      {Icon && <Icon size={iconSize || 'md'} />}
      <Flex
        justifyContent="flex-start"
        alignItems="center"
        gap={isDisabled ? 3 : 5}
        flex={1}
        minWidth={0}
      >
        <TextEllipsisWrapper wordBreak="break-all">
          <TextV2
            typographyVariant={typographyVariant}
            fontWeight={fontWeight}
            verticalAlign="middle"
          >
            {text}
          </TextV2>
        </TextEllipsisWrapper>
        {newPillText && (
          <NewBadge
            color="#FFF"
            typographyVariant={'label3'}
          >
            {newPillText}
          </NewBadge>
        )}
      </Flex>
      {visibilityIcon}
      {typeof count === 'number' &&
        count > 0 &&
        (countIsWarning ? (
          <Flex
            alignItems="center"
            gap={1}
            paddingX={2}
            paddingY="2px"
            borderRadius="999px"
            backgroundColor="negative6"
            color="white"
          >
            <WarningTriangle size={12} />
            {count}
          </Flex>
        ) : (
          <Badge
            variant="counter"
            count={count}
            size="sm"
            backgroundColor="mono14"
          />
        ))}
      {notificationPill && <NotificationPill typographyVariant={'label3'}>{notificationPill}</NotificationPill>}
      {expands && (
        <ChevronRight
          size="sm"
          position="absolute"
          right={0}
          marginRight={pxToRem(8)}
        />
      )}
    </Flex>
  );
  return (
    <>
      {topDivider && (
        <Divider
          marginY={pxToRem(4)}
          marginLeft={5}
          marginRight={5}
        />
      )}
      <Flex
        alignItems="center"
        onClick={handleOnClick}
        as={path ? (pathIsInJoyWeb ? Link : 'a') : 'div'}
        {...(path && (pathIsInJoyWeb ? { to: path } : { href: path }))}
      >
        <StyledItemWrapper
          content={content}
          textColor={effectiveColor}
        />
      </Flex>
      {bottomDivider && (
        <Divider
          marginY={pxToRem(4)}
          marginLeft={5}
          marginRight={5}
        />
      )}
    </>
  );
};
